import { noForwardProps } from '@apeiron/temp'
import { ApeironLoadingNow } from '@apeiron/ui'
import {
  Box,
  Button,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import RentalEditRow from '@src/components/orbitalTrack/rentalSetting/common/RentalEditRow'
import RentalSettingDialog, {
  RentalType
} from '@src/components/orbitalTrack/rentalSetting/common/RentalSettingDialog'
import { PriorityEditDialog } from '@src/components/orbitalTrack/rentalSetting/inviteRental/PriorityEditDialog'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(Box)`
  background: linear-gradient(90deg, #1a1a1a 0%, #000000 100%);
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Header = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  column-gap: 20px;
  row-gap: 10px;
  padding: 15px;
  align-items: center;

  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 16px;
  }

  ${props => props.theme.breakpoints.up('res480')} {
    padding: 15px 20px 15px 36px;
  }

  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 15px 20px 15px 92px;
  }
`

const Title = styled(Typography)`
  font-size: 20px;

  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 16px;
  }
  font-weight: 500;
  line-height: 20px;
`

const NewButtonContainer = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  ${props => props.theme.breakpoints.down('res768')} {
    /* font-size: 16px; */
    width: 100%;
  }
`

const InvitedCount = styled(Typography)`
  font-weight: 700;

  strong {
    color: #ffbf36;
    font-weight: 700;
  }
`

const NewButton = styled(Button, noForwardProps)`
  background: linear-gradient(32deg, #57b04f 0%, #2b7833 100%);
  border-radius: 4px;
  color: white;
  font-size: 18px;
  font-weight: 400;
  margin-top: 5px;
  max-width: 265px;
  text-transform: none;
  padding-left: 20px;
  padding-right: 20px;
  height: 32px;

  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
  }
`

const InviteRentalList = () => {
  const { trackDetail, refetchStar, loading } = useOrbitalTrackAttachContext()

  const [newSettingDialogOpen, setNewSettingDialogOpen] = useState(false)

  const theme = useTheme()

  const under768 = useMediaQuery(theme.breakpoints.down('res768'))

  const [priorityEditDialogOpen, setPriorityEditDialogOpen] = useState(false)

  const { t } = useTranslation()

  const handleNewButtonClick = () => {
    setNewSettingDialogOpen(true)
  }

  const handleDialogClose = useCallback(
    (success: boolean) => {
      setNewSettingDialogOpen(false)
      if (success) {
        refetchStar()
      }
    },
    [refetchStar]
  )

  return (
    <Container>
      <Header>
        <Title>
          {t('godiverse.orbital_track.invite_track')}
          {' - '}
          <InvitedCount as='span'>
            <Trans>
              {t('godiverse.orbital_track.total_invited_group', {
                count: trackDetail?.inviteSettings.length,
                maxLimit: 50,
                context: under768 ? 'mobile' : undefined
              })}
            </Trans>
          </InvitedCount>
        </Title>
        <NewButtonContainer>
          {under768 && <Box>{t('godiverse.orbital_track.invite_group')}</Box>}
          <NewButton onClick={handleNewButtonClick}>
            {t('godiverse.orbital_track.new_invite')}
          </NewButton>
        </NewButtonContainer>
      </Header>
      <Box>
        {!loading ? (
          trackDetail?.inviteSettings.map((inviteSetting, index) => {
            return (
              <RentalEditRow
                key={inviteSetting.inviteGroupId}
                rentalType={RentalType.INVITE}
                displayedPriority={index + 1}
                onPriorityEditClick={() => setPriorityEditDialogOpen(true)}
                groupId={inviteSetting.inviteGroupId}
              />
            )
          })
        ) : (
          <ApeironLoadingNow />
        )}
      </Box>
      <RentalSettingDialog
        rentalType={RentalType.INVITE}
        open={newSettingDialogOpen}
        onClose={handleDialogClose}
      />
      {priorityEditDialogOpen && (
        <PriorityEditDialog
          open={priorityEditDialogOpen}
          onClose={() => setPriorityEditDialogOpen(false)}
        />
      )}
    </Container>
  )
}

export default InviteRentalList
